import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { HeartHandshake, Recycle, Sparkles, Check, ArrowRight } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const CHOICES = [
  {
    id: "general",
    icon: HeartHandshake,
    title: "General shelter needs",
    body: "Dog walks, kennel care, feeding, cleaning — the day-to-day help the dogs depend on.",
  },
  {
    id: "cans",
    icon: Recycle,
    title: "Cans for Canines Recycling Program",
    body: "Collect, sort, and drop off bottles & cans — every nickel goes straight to the dogs.",
  },
  {
    id: "events",
    icon: Sparkles,
    title: "Special events only",
    body: "Adoption fairs, fundraisers, pop-ups — I'd love to help when something fun is on the calendar.",
  },
];

export default function Welcome() {
  const nav = useNavigate();
  const { user, setUser } = useAuth();
  const [picked, setPicked] = useState(new Set(user?.interests || []));
  const [busy, setBusy] = useState(false);

  const toggle = (id) => {
    const next = new Set(picked);
    if (next.has(id)) next.delete(id); else next.add(id);
    setPicked(next);
  };

  const save = async (interests) => {
    setBusy(true);
    try {
      const { data } = await api.patch("/me/interests", { interests });
      setUser(data);
      toast.success("Thanks — we'll match shifts to what you picked.");
      nav("/waiver");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const submit = () => {
    if (picked.size === 0) {
      toast.error("Pick at least one so we know how to help you help the dogs.");
      return;
    }
    save(Array.from(picked));
  };

  const skip = () => save([]);

  return (
    <div className="min-h-screen bg-[color:var(--cream)]">
      <header className="border-b border-[color:var(--border)] bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-4">
          <a href="https://hoodriveradoptadog.org" target="_blank" rel="noopener noreferrer" data-testid="welcome-logo-link" className="inline-flex items-center">
            <img src="/logo.png" alt="Hood River Adopt A Dog" className="h-14 w-auto" />
          </a>
        </div>
      </header>
      <div className="max-w-3xl mx-auto px-5 lg:px-8 pt-12 pb-20">
        <div className="mb-10">
          <div className="text-xs uppercase tracking-wider text-[color:var(--stone)] mb-2">Welcome{user?.name ? `, ${user.name.split(" ")[0]}` : ""}</div>
          <h1 className="font-display text-4xl sm:text-5xl leading-tight">What would you like to help with?</h1>
          <p className="mt-3 font-semibold text-[color:var(--red)] max-w-xl">Pick one or a few. We&rsquo;ll point you at the shifts and emails that match, so you&rsquo;re only hearing about the things you care about.</p>
        </div>

        <div className="grid gap-3 mb-8" data-testid="welcome-choices">
          {CHOICES.map((c) => {
            const Icon = c.icon;
            const active = picked.has(c.id);
            return (
              <button
                key={c.id}
                type="button"
                data-testid={`welcome-choice-${c.id}`}
                onClick={() => toggle(c.id)}
                aria-pressed={active}
                className={`text-left rounded-3xl border-2 p-5 sm:p-6 transition-colors ${active ? "border-[color:var(--red)] bg-[color:var(--red)]/5" : "border-[color:var(--border)] bg-white hover:bg-[color:var(--cream-2)]"}`}
              >
                <div className="flex items-start gap-4">
                  <div className={`h-11 w-11 shrink-0 rounded-2xl flex items-center justify-center ${active ? "bg-[color:var(--red)] text-white" : "bg-[color:var(--cream-2)] text-[color:var(--ink)]"}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3">
                      <div className="font-display text-lg sm:text-xl">{c.title}</div>
                      <div className={`h-6 w-6 rounded-full border-2 flex items-center justify-center ${active ? "border-[color:var(--red)] bg-[color:var(--red)]" : "border-[color:var(--border)]"}`}>
                        {active && <Check className="h-4 w-4 text-white" strokeWidth={3} />}
                      </div>
                    </div>
                    <p className="mt-1 text-sm text-[color:var(--stone)]">{c.body}</p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <button
            data-testid="welcome-continue-btn"
            disabled={busy}
            onClick={submit}
            className="btn-pill btn-primary inline-flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {busy ? "Saving…" : "Continue to waiver"}
            {!busy && <ArrowRight className="h-4 w-4" />}
          </button>
          <button
            data-testid="welcome-skip-btn"
            disabled={busy}
            onClick={skip}
            className="btn-pill btn-ghost-ink text-sm disabled:opacity-60"
          >
            Skip for now
          </button>
          <span className="text-xs text-[color:var(--stone)] sm:ml-auto">You can change this any time from your profile.</span>
        </div>
      </div>
    </div>
  );
}
